/**
 * The Connect control's state on the desktop (docs/DESIGN-asana-connect-from-gyro.md sections 1, 3 and 4): one flow per
 * vendor family, started by a click, polled through GET /v1/person/connections under the bounds of section 3 (5 s floor,
 * 15 s after a minute, 48 ticks at most, stopped at connected, at pending false, at expiresAt, and paused while the
 * surface is off screen), and the person's connection state for the Connectors page. The desktop's native layer opens
 * the browser and never hands the renderer a URL; this hook only ever sees `{ status, family, expiresAt, pollAfterMs }`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parsePersonConnectStart, parsePersonConnections, personConnectPollDelayMs, personConnectPollStop, PERSON_CONNECT_NOT_SWITCHED_ON, } from './personConnect.js';
/**
 * Refusals that mean connecting is not switched on for this PC or this service, so the Connectors rows stay hidden
 * (DesktopConnectorList): the receiver not registered or not offered, the Bridge's family not configured, its receiver
 * switched off or its person pair not armed (PERSON_CONNECT_NOT_SWITCHED_ON), a PC without the person-token scope (401
 * gyro_person_token_required), and a gyro-svc older than the routes (404).
 */
const UNAVAILABLE = new Set([...PERSON_CONNECT_NOT_SWITCHED_ON, 'gyro_person_token_required', 'not_found']);
export function usePersonConnect(client, visible, enabled) {
    const [views, setViews] = useState({});
    const [connections, setConnections] = useState({ status: 'idle' });
    const open = useRef({});
    const live = useRef(true);
    const visibleRef = useRef(visible);
    visibleRef.current = visible;
    const setView = useCallback((family, view) => {
        if (!live.current)
            return;
        setViews((current) => ({ ...current, [family]: { family, ...view } }));
    }, []);
    const endFlow = useCallback((family) => {
        const flow = open.current[family];
        if (!flow)
            return;
        if (flow.timer !== undefined)
            clearTimeout(flow.timer);
        flow.abort?.abort();
        delete open.current[family];
    }, []);
    /** One read of the person's connections; the poll and the Connectors page share it (gyro-svc caches it per person). */
    const read = useCallback(async (signal) => {
        try {
            const { status, body } = await client.personConnections({ signal });
            if (status === 200)
                return { status, connections: parsePersonConnections(body) ?? {} };
            // A 404 is a service without the route (Fastify's own body, no code of ours): read as not_found whatever it says.
            if (status === 404)
                return { status, code: 'not_found' };
            const code = body && typeof body === 'object' && typeof body.error === 'string' ? body.error : 'failed';
            return { status, code };
        }
        catch {
            return { status: 0, code: 'bridge_unavailable' };
        }
    }, [client]);
    const refresh = useCallback(() => {
        if (!enabled)
            return;
        setConnections((current) => ({ ...current, status: current.status === 'ready' ? 'ready' : 'loading' }));
        void read().then((answer) => {
            if (!live.current)
                return;
            if (answer.connections)
                setConnections({ status: 'ready', connections: answer.connections });
            else
                setConnections({ status: answer.code && UNAVAILABLE.has(answer.code) ? 'unavailable' : 'error', code: answer.code });
        });
    }, [enabled, read]);
    const tick = useCallback((family) => {
        const flow = open.current[family];
        if (!flow || !live.current)
            return;
        flow.timer = undefined;
        // Off screen or locked: no tick now; the surface's return schedules exactly one (the effect below).
        if (!visibleRef.current)
            return;
        flow.ticks += 1;
        const abort = new AbortController();
        flow.abort = abort;
        void read(abort.signal).then((answer) => {
            if (!live.current || open.current[family] !== flow || abort.signal.aborted)
                return;
            flow.abort = undefined;
            if (answer.connections)
                setConnections({ status: 'ready', connections: answer.connections });
            const stop = personConnectPollStop({ family, capability: flow.capability, connections: answer.connections, expiresAtMs: flow.expiresAtMs, nowMs: Date.now(), ticks: flow.ticks });
            if (stop) {
                endFlow(family);
                setView(family, stop === 'connected' ? { phase: 'connected' } : { phase: 'idle' });
                return;
            }
            flow.timer = setTimeout(() => tick(family), personConnectPollDelayMs(flow.startedAt, Date.now(), flow.pollAfterMs));
        });
    }, [endFlow, read, setView]);
    const start = useCallback((connect) => {
        if (!enabled)
            return;
        const { family } = connect;
        if (open.current[family]) {
            // A second press while the flow is open sends nothing: the page keeps waiting on the one browser tab.
            setView(family, { phase: 'waiting' });
            return;
        }
        setView(family, { phase: 'starting' });
        void client.connectStart(family, connect.capability === 'write' ? 'write' : undefined).then(({ status, body }) => {
            if (!live.current)
                return;
            const answer = parsePersonConnectStart(status, body);
            if (answer.kind === 'connected') {
                setView(family, { phase: 'connected' });
                refresh();
                return;
            }
            if (answer.kind === 'error') {
                setView(family, { phase: 'error', code: answer.code });
                return;
            }
            const flow = { family, capability: connect.capability === 'write' ? 'write' : 'read', startedAt: Date.now(), expiresAtMs: answer.expiresAtMs, pollAfterMs: answer.pollAfterMs, ticks: 0 };
            open.current[family] = flow;
            setView(family, { phase: 'waiting' });
            flow.timer = setTimeout(() => tick(family), answer.pollAfterMs);
        }, () => {
            if (live.current)
                setView(family, { phase: 'error', code: 'bridge_unavailable' });
        });
    }, [client, enabled, refresh, setView, tick]);
    const change = useCallback(async (family, action) => {
        if (!enabled)
            return { ok: false, code: 'person_receiver_unavailable' };
        try {
            const { status, body } = await client.changePersonConnection(family, action);
            const code = body && typeof body === 'object' && typeof body.error === 'string' ? body.error : 'failed';
            // Nothing left to remove (the Bridge's 409 nothing_to_remove, or the family's not-connected code): already done.
            const alreadyDone = status === 409 && (code === 'nothing_to_remove' || code === `${family}_not_connected`);
            if (status === 200 || alreadyDone) {
                endFlow(family);
                setView(family, { phase: 'idle' });
                refresh();
                return { ok: true };
            }
            return { ok: false, code };
        }
        catch {
            return { ok: false, code: 'bridge_unavailable' };
        }
    }, [client, enabled, endFlow, refresh, setView]);
    // Coming back on screen: one tick for each open flow whose timer was skipped while hidden (then its own schedule).
    useEffect(() => {
        if (!visible)
            return;
        for (const family of Object.keys(open.current)) {
            const flow = open.current[family];
            if (flow && flow.timer === undefined && !flow.abort)
                flow.timer = setTimeout(() => tick(family), 0);
        }
    }, [tick, visible]);
    useEffect(() => {
        live.current = true;
        return () => {
            live.current = false;
            for (const family of Object.keys(open.current))
                endFlow(family);
        };
    }, [endFlow]);
    const flowFor = useCallback((family) => views[family] ?? { phase: 'idle', family }, [views]);
    return useMemo(() => ({ enabled, flowFor, start, connections, refresh, change }), [change, connections, enabled, flowFor, refresh, start]);
}
//# sourceMappingURL=usePersonConnect.js.map